import React from 'react'
import { Box, Typography, SxProps, Theme } from '@mui/material'

export interface EmptyStateProps {
  /** Main icon element */
  icon?: React.ReactNode
  /** Main empty state title */
  title: string
  /** Subtitle or explanatory text */
  description?: string
  /** Action button or element */
  action?: React.ReactNode
  /** Container minHeight (default: 320) */
  minHeight?: number | string
  /** Compact padding variant */
  compact?: boolean
  /** Additional SX overrides */
  sx?: SxProps<Theme>
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  minHeight = 320,
  compact = false,
  sx,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        p: compact ? 3 : 4,
        minHeight,
        width: '100%',
        height: '100%',
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: compact ? 56 : 64,
            height: compact ? 56 : 64,
            borderRadius: '50%',
            bgcolor: 'rgba(59, 130, 246, 0.08)',
            color: 'primary.main',
            mb: 2,
            border: '1px solid rgba(59, 130, 246, 0.15)',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.08)',
            '& svg': {
              fontSize: compact ? 28 : 32,
            },
          }}
        >
          {icon}
        </Box>
      )}

      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: '-0.3px',
          fontSize: compact ? '1rem' : '1.15rem',
          mb: description ? 0.75 : action ? 2 : 0,
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            maxWidth: 420,
            lineHeight: 1.5,
            mb: action ? 2.5 : 0,
            fontSize: '0.875rem',
          }}
        >
          {description}
        </Typography>
      )}

      {action && <Box sx={{ mt: description ? 0 : 1 }}>{action}</Box>}
    </Box>
  )
}

export default EmptyState
